import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { ClipLoader } from "react-spinners";
import { useMutation } from "@tanstack/react-query";
import { addAdminUser } from "../../services/userService";

const adminSchema = z.object({
  firstName: z.string().min(1, "First name is required"),
  lastName: z.string().min(1, "Last name is required"),
  email: z.string().email("Invalid email address"),
  password: z.string().min(6, "Password must be at least 6 characters"),
});

type AdminFormData = z.infer<typeof adminSchema>;

interface AddAdminProps {
  onClose: () => void;
  onAdminAdded: () => void;
}

export const AddAdmin = ({ onClose, onAdminAdded }: AddAdminProps) => {
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<AdminFormData>({
    resolver: zodResolver(adminSchema),
    mode: "onChange",
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      password: "",
    },
  });

  const mutation = useMutation({
    mutationFn: addAdminUser,
    onSuccess: (data) => {
      onAdminAdded();
      onClose();
    },
    onError: (error) => {
      console.error("Error adding admin:", error);
      alert("Failed to add admin. Please try again.");
    },
  });

  const handleAddAdmin = async (data: AdminFormData) => {
    try {
      await mutation.mutateAsync(data);
    } catch (error) {
      console.error("Error adding admin:", error);
    }
  };

  return (
    <div className="fixed inset-0 p-2 flex items-center justify-center bg-gray-800 bg-opacity-50 z-50">
      <div className="bg-white p-6 rounded-lg shadow-lg w-96">
        <h2 className="text-xl mb-4">New Admin</h2>
        <form onSubmit={handleSubmit(handleAddAdmin)} className="space-y-3">
          {/* First Name */}
          <div className="flex flex-col w-full">
            <label className="text-sm text-gray-500">First Name</label>
            <input
              className="border p-2 outline-none bg-gray-100 rounded-lg"
              type="text"
              {...register("firstName")}
            />
            {errors.firstName && (
              <p className="text-rose-500 text-sm mt-1">
                {errors.firstName.message}
              </p>
            )}
          </div>

          {/* Last Name */}
          <div className="flex flex-col w-full">
            <label className="text-sm text-gray-500">Last Name</label>
            <input
              className="border p-2 outline-none bg-gray-100 rounded-lg"
              type="text"
              {...register("lastName")}
            />
            {errors.lastName && (
              <p className="text-rose-500 text-sm mt-1">
                {errors.lastName.message}
              </p>
            )}
          </div>

          {/* Email */}
          <div className="flex flex-col w-full">
            <label className="text-sm text-gray-500">Email</label>
            <input
              className="border p-2 outline-none bg-gray-100 rounded-lg"
              type="email"
              {...register("email")}
            />
            {errors.email && (
              <p className="text-rose-500 text-sm mt-1">
                {errors.email.message}
              </p>
            )}
          </div>

          {/* Password */}
          <div className="flex flex-col w-full">
            <label className="text-sm text-gray-500">Password</label>
            <input
              className="border p-2 outline-none bg-gray-100 rounded-lg"
              type="password"
              {...register("password")}
            />
            {errors.password && (
              <p className="text-rose-500 text-sm mt-1">
                {errors.password.message}
              </p>
            )}
          </div>

          <div className="flex justify-end space-x-2 mt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-rose-500 rounded-lg text-sm"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="border px-4 py-2 rounded-lg text-sm flex items-center justify-center"
              disabled={isSubmitting}
            >
              {isSubmitting ? <ClipLoader size={20} /> : "Add Admin"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
